import { 
    collection, 
    addDoc, 
    updateDoc, 
    deleteDoc, 
    doc, 
    getDocs, 
    query, 
    where,
    Timestamp,
    orderBy,
    limit,
    startAt,
    enableNetwork,
    disableNetwork,
    getDoc,
    setDoc,
    serverTimestamp,
    runTransaction
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../firebase/config';
import { db } from '../firebase/config';
import { Appointment } from '../types/appointment';
import type { TimeSlot } from '../types/timeSlot';
import { format, setHours, setMinutes, startOfDay, endOfDay, parseISO } from 'date-fns';
import { tr } from 'date-fns/locale';
import { authService } from './authService';
import { spreadsService } from './spreadsService';
import { errorHandler } from '../utils/errorHandler';

const COLLECTION_NAME = 'appointments';

interface DaySchedule {
    start: string;
    end: string;
    isActive: boolean;
}

export interface WorkingHours {
    start: string;
    end: string;
}

export type TimeSlotStatus = 'available' | 'booked' | 'blocked';

export interface WorkingHoursSettings {
    default: WorkingHours;
    specificDays: {
        [key: string]: WorkingHours;
    };
}

// Cache mekanizması
const cache = new Map<string, { data: any; timestamp: number }>();
const CACHE_DURATION = 5 * 60 * 1000; // 5 dakika

const isCacheValid = (key: string) => {
    const cached = cache.get(key);
    if (!cached) return false;
    return Date.now() - cached.timestamp < CACHE_DURATION;
};

export interface CreateAppointmentData {
    name: string;
    phone: string;
    email: string;
    userId: string;
    date: string;
    time: string;
    spreadId: string;
    notes?: string;
    status: 'pending' | 'confirmed' | 'completed' | 'rejected';
    paymentStatus: 'waiting_confirmation' | 'confirmed' | 'cancelled';
    paymentType: 'regular' | 'points';
    receiptFileId?: string;
    receiptUrl?: string;
    spreadPoints?: number;
}

// Appointment tipini güncelle
interface AppointmentData {
    date: string;
    email: string;
    name: string;
    notes: string;
    paymentStatus: 'waiting_confirmation' | 'confirmed' | 'cancelled';
    paymentType: 'regular' | 'points';
    phone: string;
    rejectionReason?: string;
    spreadId: string;
    spreadPoints?: number;
    status: 'pending' | 'confirmed' | 'completed' | 'rejected';
    time: string;
    userId: string;
}

export interface RawAppointmentData {
    id: string;
    userId: string;
    name: string;
    email: string;
    phone: string;
    date: string;
    time: string;
    status: string;
    paymentType: string;
    paymentStatus: 'waiting_confirmation' | 'confirmed' | 'cancelled';
    notes: string;
    receiptUrl: string;
    receiptFileId: string;
    createdAt: Timestamp;
    updatedAt: Timestamp;
    spreadId?: string;
    spreadPoints?: number;
    rejectionReason?: string;
}

export const workingHoursService = {
    // Çalışma saatlerini getir
    getWorkingHours: async (): Promise<WorkingHours> => {
        try {
            const settingsRef = doc(db, 'settings', 'workingHours');
            const docSnap = await getDoc(settingsRef);
            
            if (!docSnap.exists()) {
                return {
                    start: '13:00',
                    end: '22:00'
                };
            }

            const data = docSnap.data();
            
            // Veritabanı yapısına uygun olarak default değerleri al
            return {
                start: data.default?.start || '13:00',
                end: data.default?.end || '22:00'
            };
        } catch (error) {
            errorHandler.handleError(error, 'Error getting working hours');
            return {
                start: '13:00',
                end: '22:00'
            };
        }
    },

    // Belirli bir tarih için çalışma saatlerini getir
    getWorkingHoursForDate: async (date: string): Promise<WorkingHours> => {
        try {
            const settingsRef = doc(db, 'settings', 'workingHours');
            const docSnap = await getDoc(settingsRef);
            
            if (!docSnap.exists()) {
                errorHandler.log('No working hours settings found, using defaults');
                return {
                    start: '13:00',
                    end: '22:00'
                };
            }

            const data = docSnap.data();
            errorHandler.log('Working hours settings:', data);
            
            // Önce belirli tarih için özel saat kontrolü
            if (data.specificDays && data.specificDays[date]) {
                errorHandler.log('Found specific hours for date:', date, data.specificDays[date]);
                return {
                    start: data.specificDays[date].start,
                    end: data.specificDays[date].end
                };
            }
            
            // Özel saat yoksa varsayılan saatleri kullan
            if (data.default) {
                errorHandler.log('Using default hours:', data.default);
                return {
                    start: data.default.start,
                    end: data.default.end
                };
            }

            // Hiçbir ayar bulunamazsa varsayılan değerleri kullan
            errorHandler.log('No valid settings found, using hardcoded defaults');
            return {
                start: '13:00',
                end: '22:00'
            };
        } catch (error) {
            errorHandler.handleError(error, 'Error getting working hours for date');
            return {
                start: '13:00',
                end: '22:00'
            };
        }
    },

    // Çalışma saatlerini güncelle
    updateWorkingHours: async (workingHours: WorkingHours): Promise<void> => {
        try {
            const settingsRef = doc(db, 'settings', 'workingHours');
            const docSnap = await getDoc(settingsRef);
            
            if (docSnap.exists()) {
                const data = docSnap.data();
                
                await setDoc(settingsRef, {
                    ...data,
                    default: {
                        start: workingHours.start,
                        end: workingHours.end
                    }
                });
            } else {
                await setDoc(settingsRef, {
                    default: {
                        start: workingHours.start,
                        end: workingHours.end
                    },
                    specificDays: {}
                });
            }
        } catch (error) {
            throw error;
        }
    },

    // Çalışma saatleri formatını güncelle
    migrateWorkingHoursFormat: async (): Promise<void> => {
        try {
            const settingsRef = doc(db, 'settings', 'workingHours');
            const docSnap = await getDoc(settingsRef);
            
            if (!docSnap.exists()) {
                return;
            }

            const data = docSnap.data();
            const newFormat: WorkingHours = {
                start: '13:00',
                end: '22:00'
            };

            await setDoc(settingsRef, newFormat);
        } catch (error) {
            throw error;
        }
    },

    // Müsait randevu saatlerini getir
    getTimeSlots: async (date: string): Promise<TimeSlot[]> => {
        try {
            const timeSlotsRef = collection(db, 'timeSlots');
            const q = query(timeSlotsRef, where('date', '==', date));
            const querySnapshot = await getDocs(q);
            
            return querySnapshot.docs.map(doc => ({
                id: doc.id,
                date: doc.data().date,
                time: doc.data().time,
                status: doc.data().status || 'available',
                isActive: doc.data().isActive ?? true
            }));
        } catch (error) {
            throw error;
        }
    },

    // Randevu saati ekle
    addTimeSlot: async (timeSlot: Omit<TimeSlot, 'id'>): Promise<string> => {
        try {
            const timeSlotsRef = collection(db, 'timeSlots');
            const docRef = await addDoc(timeSlotsRef, {
                ...timeSlot,
                date: format(new Date(timeSlot.date), 'yyyy-MM-dd')
            });
            return docRef.id;
        } catch (error) {
            errorHandler.handleError(error, 'Error adding time slot');
            throw error;
        }
    },

    // Randevu saatlerini toplu ekle
    addTimeSlots: async (timeSlots: Array<Omit<TimeSlot, 'id'>>): Promise<string[]> => {
        try {
            if (!timeSlots || timeSlots.length === 0) {
                throw new Error('Eklenecek randevu saati bulunamadı');
            }

            errorHandler.log('Adding time slots to Firestore:', timeSlots);
            
            const timeSlotsRef = collection(db, 'timeSlots');
            const promises = timeSlots.map(timeSlot => {
                // Her bir slot için veri doğrulaması yap
                if (!timeSlot.date || !timeSlot.time) {
                    throw new Error('Geçersiz randevu saati verisi');
                }
                
                // Saati kontrol et (sadece tam saatlere izin ver)
                const [hour, minute] = timeSlot.time.split(':').map(Number);
                if (minute !== 0) {
                    throw new Error('Sadece tam saatlik randevular eklenebilir (örn: 13:00, 14:00)');
                }
                
                const slotData = {
                    ...timeSlot,
                    time: `${hour.toString().padStart(2, '0')}:00`, // Dakikayı sıfırla
                    createdAt: Timestamp.now(),
                    updatedAt: Timestamp.now(),
                    isActive: true,
                    status: 'available'
                };
                
                return addDoc(timeSlotsRef, slotData);
            });
            
            const results = await Promise.all(promises);
            errorHandler.log('Successfully added time slots, IDs:', results.map(r => r.id));
            return results.map(docRef => docRef.id);
        } catch (error) {
            errorHandler.handleError(error, 'Error adding time slots');
            throw error;
        }
    },

    // Randevu saati güncelle
    updateTimeSlot: async (id: string, timeSlot: Partial<TimeSlot>): Promise<void> => {
        try {
            const timeSlotRef = doc(db, 'timeSlots', id);
            const updateData = { ...timeSlot };
            if (timeSlot.date) {
                updateData.date = format(new Date(timeSlot.date), 'yyyy-MM-dd');
            }
            await updateDoc(timeSlotRef, updateData);
        } catch (error) {
            throw error;
        }
    },

    // Randevu saati durumunu güncelle
    updateTimeSlotStatus: async (id: string, status: TimeSlotStatus): Promise<void> => {
        try {
            const timeSlotRef = doc(db, 'timeSlots', id);
            await updateDoc(timeSlotRef, { status });
        } catch (error) {
            throw error;
        }
    },

    // Randevu saati sil
    deleteTimeSlot: async (id: string): Promise<void> => {
        try {
            const timeSlotRef = doc(db, 'timeSlots', id);
            await deleteDoc(timeSlotRef);
        } catch (error) {
            errorHandler.handleError(error, 'Error deleting time slot');
            throw error;
        }
    },

    // Randevu koleksiyonunu kontrol et
    checkAppointmentsCollection: async (): Promise<void> => {
        try {
            const appointmentsRef = collection(db, 'appointments');
            await getDocs(appointmentsRef);
        } catch (error) {
            throw error;
        }
    }
};

export const timeSlotService = {
    // Get all time slots
    getTimeSlots: async (): Promise<TimeSlot[]> => {
        try {
            const timeSlotsRef = collection(db, 'timeSlots');
            const querySnapshot = await getDocs(timeSlotsRef);
            
            return querySnapshot.docs.map(doc => {
                const data = doc.data();
                return {
                    id: doc.id,
                    date: data.date instanceof Timestamp ? format(data.date.toDate(), 'yyyy-MM-dd') : data.date,
                    time: data.time instanceof Timestamp ? format(data.time.toDate(), 'HH:mm') : data.time,
                    status: data.status || 'available',
                    isActive: data.isActive ?? true
                };
            });
        } catch (error) {
            errorHandler.handleError(error, 'Error getting time slots');
            return [];
        }
    },

    // Add multiple time slots
    addTimeSlots: async (timeSlots: Array<Omit<TimeSlot, 'id'>>): Promise<string[]> => {
        try {
            if (!timeSlots || timeSlots.length === 0) {
                throw new Error('Eklenecek randevu saati bulunamadı');
            }

            errorHandler.log('Adding time slots to Firestore:', timeSlots);
            
            const timeSlotsRef = collection(db, 'timeSlots');
            const promises = timeSlots.map(timeSlot => {
                // Her bir slot için veri doğrulaması yap
                if (!timeSlot.date || !timeSlot.time) {
                    throw new Error('Geçersiz randevu saati verisi');
                }
                
                const slotData = {
                    ...timeSlot,
                    createdAt: Timestamp.now(),
                    updatedAt: Timestamp.now()
                };
                
                return addDoc(timeSlotsRef, slotData);
            });
            
            const results = await Promise.all(promises);
            errorHandler.log('Successfully added time slots, IDs:', results.map(r => r.id));
            return results.map(docRef => docRef.id);
        } catch (error) {
            errorHandler.handleError(error, 'Error adding time slots');
            throw error;
        }
    },

    // Update time slot status
    updateTimeSlotStatus: async (id: string, isActive: boolean): Promise<void> => {
        try {
            const timeSlotRef = doc(db, 'timeSlots', id);
            await updateDoc(timeSlotRef, { isActive });
        } catch (error) {
            errorHandler.handleError(error, 'Error updating time slot status');
            throw error;
        }
    },

    // Delete time slot
    deleteTimeSlot: async (id: string): Promise<void> => {
        try {
            const timeSlotRef = doc(db, 'timeSlots', id);
            await deleteDoc(timeSlotRef);
        } catch (error) {
            errorHandler.handleError(error, 'Error deleting time slot');
            throw error;
        }
    }
};

// Dekont URL'si alma fonksiyonu
const getReceiptDownloadUrl = async (fileName: string): Promise<string> => {
    try {
        const storageRef = ref(storage, fileName);
        return await getDownloadURL(storageRef);
    } catch (error) {
        errorHandler.handleError(error, 'Error getting receipt download URL');
        throw error;
    }
};

export const appointmentService = {
    // Koleksiyonun varlığını kontrol et
    checkCollectionExists: async (): Promise<boolean> => {
        try {
            const q = query(collection(db, COLLECTION_NAME), limit(1));
            const snapshot = await getDocs(q);
            return !snapshot.empty;
        } catch (error) {
            errorHandler.handleError(error, 'Error checking appointments collection');
            return false;
        }
    },

    // Bağlantı yönetimi
    goOffline: async () => {
        await disableNetwork(db);
    },

    goOnline: async () => {
        await enableNetwork(db);
    },

    // Randevu oluştur
    create: async (data: CreateAppointmentData): Promise<void> => {
        try {
            // If this is a points-based appointment
            if (data.paymentType === 'points') {
                // Get the spread details to verify points
                const spreadRef = doc(db, 'spreads', data.spreadId);
                const spreadDoc = await getDoc(spreadRef);

                if (!spreadDoc.exists()) {
                    throw new Error('Fal tipi bulunamadı');
                }

                const requiredPoints = spreadDoc.data().price || 0;

                // Get user's current points
                const userRef = doc(db, 'users', data.userId);
                const userDoc = await getDoc(userRef);

                if (!userDoc.exists()) {
                    throw new Error('Kullanıcı bulunamadı');
                }

                const currentPoints = userDoc.data().points || 0;

                // Check if user has enough points
                if (currentPoints < requiredPoints) {
                    throw new Error('Yetersiz puan');
                }

                // Use transaction to create appointment and deduct points atomically
                await runTransaction(db, async (transaction) => {
                    // Deduct points from user immediately
                    transaction.update(userRef, {
                        points: currentPoints - requiredPoints,
                        updatedAt: serverTimestamp()
                    });

                    // Create the appointment with points already deducted
                    const appointmentRef = doc(collection(db, COLLECTION_NAME));
                    transaction.set(appointmentRef, {
                        ...data,
                        spreadPoints: requiredPoints,
                        pointsDeducted: true, // Flag to track that points were deducted
                        createdAt: serverTimestamp(),
                        updatedAt: serverTimestamp(),
                        paymentStatus: 'confirmed' // Mark payment as confirmed since points are deducted
                    });
                });

                // Add points history entry
                await authService.addPointsHistory(
                    data.userId,
                    -requiredPoints,
                    'Puan ile randevu talebi oluşturuldu',
                    data.spreadId
                );
            } else {
                // Regular payment appointment
                const appointmentRef = collection(db, COLLECTION_NAME);
                await addDoc(appointmentRef, {
                    ...data,
                    createdAt: serverTimestamp(),
                    updatedAt: serverTimestamp()
                });
            }
        } catch (error) {
            errorHandler.handleError(error, 'Error creating appointment');
            throw error;
        }
    },

    // Randevu güncelle
    update: async (id: string, appointment: Partial<Appointment>): Promise<void> => {
        try {
            const docRef = doc(db, COLLECTION_NAME, id);
            await updateDoc(docRef, {
                ...appointment,
                updatedAt: Timestamp.now()
            });
        } catch (error) {
            throw error;
        }
    },

    // Randevu durumunu güncelle
    updateStatus: async (id: string, status: Appointment['status'], rejectionReason?: string): Promise<void> => {
        try {
            const docRef = doc(db, COLLECTION_NAME, id);
            const appointmentDoc = await getDoc(docRef);
            
            if (!appointmentDoc.exists()) {
                errorHandler.log('❌ Appointment not found:', id);
                throw new Error('Randevu bulunamadı');
            }

            const data = appointmentDoc.data();
            errorHandler.log('📝 Current appointment data:', JSON.stringify(data, null, 2));

            // Kullanıcı ID'sini kontrol et
            if (!data.userId) {
                errorHandler.log('❌ No userId found in appointment data');
                throw new Error('Randevu verisinde kullanıcı ID bulunamadı');
            }

            const appointmentData: Appointment = {
                id: appointmentDoc.id,
                userId: data.userId,
                userName: data.name || '',
                userEmail: data.email || '',
                userPhone: data.phone || '',
                date: data.date || '',
                time: data.time || '',
                status: data.status || 'pending',
                paymentType: (data.paymentType || 'regular') as 'regular' | 'points',
                paymentStatus: data.paymentStatus || 'waiting_confirmation',
                notes: data.notes || '',
                receiptUrl: data.receiptUrl || '',
                receiptFileId: data.receiptFileId || '',
                spreadId: data.spreadId || '',
                spreadPoints: data.spreadPoints || 0,
                createdAt: data.createdAt || Timestamp.now(),
                updatedAt: data.updatedAt || Timestamp.now(),
                rejectionReason: data.rejectionReason || ''
            };

            const updateData: any = {
                status,
                updatedAt: Timestamp.now()
            };

            switch (status) {
                case 'rejected':
                    // Puan ile yapılan randevularda puanları iade et
                    if (appointmentData.paymentType === 'points' && appointmentData.status !== 'rejected') {
                        const pointsToRefund = appointmentData.spreadPoints || 0;
                        const userRef = doc(db, 'users', appointmentData.userId);
                        const userDoc = await getDoc(userRef);
                        
                        if (!userDoc.exists()) {
                            throw new Error('Kullanıcı bulunamadı');
                        }
                        
                        const currentPoints = userDoc.data().points || 0;
                        
                        // Transaction kullanarak puanları iade et
                        await runTransaction(db, async (transaction) => {
                            transaction.update(userRef, {
                                points: currentPoints + pointsToRefund,
                                updatedAt: serverTimestamp()
                            });

                            transaction.update(docRef, {
                                ...updateData,
                                rejectionReason: rejectionReason || '',
                                pointsDeducted: false // Reset the points deducted flag
                            });
                        });

                        // Add points history entry for refund
                        await authService.addPointsHistory(
                            appointmentData.userId,
                            pointsToRefund,
                            `Randevu reddedildi - ${pointsToRefund} puan iade edildi`,
                            appointmentData.id
                        );
                        
                        return; // Transaction already updated the appointment
                    }
                    break;
                case 'completed':
                    // Randevu tamamlandığında ödül puanı ekle
                    if (appointmentData.paymentType === 'regular' && appointmentData.status !== 'completed' && !data.pointsDeducted) {
                        const userRef = doc(db, 'users', appointmentData.userId);
                        const userDoc = await getDoc(userRef);
                        
                        if (!userDoc.exists()) {
                            throw new Error('Kullanıcı bulunamadı');
                        }
                        
                        const currentPoints = userDoc.data().points || 0;
                        const rewardPoints = appointmentData.spreadPoints || 0; // Ödül puanı olarak spreadPoints değerini kullan
                        
                        // Transaction kullanarak ödül puanı ekle
                        await runTransaction(db, async (transaction) => {
                            transaction.update(userRef, {
                                points: currentPoints + rewardPoints,
                                updatedAt: serverTimestamp()
                            });

                            transaction.update(docRef, {
                                ...updateData,
                                paymentStatus: 'confirmed',
                                pointsDeducted: true // İlk tamamlanmada puanların eklendiğini işaretle
                            });
                        });

                        // Add points history entry for reward
                        await authService.addPointsHistory(
                            appointmentData.userId,
                            rewardPoints,
                            'Randevu tamamlandı - Ödül puanı',
                            appointmentData.id
                        );
                        
                        return; // Transaction already updated the appointment
                    }
                    updateData.paymentStatus = 'confirmed';
                    break;
            }

            if (rejectionReason) {
                updateData.rejectionReason = rejectionReason;
            }

            await updateDoc(docRef, updateData);
            errorHandler.log('✅ Appointment update completed');
        } catch (error) {
            errorHandler.log('❌ Error in updateStatus:', error);
            errorHandler.handleError(error, 'Error updating appointment status');
            throw error;
        }
    },

    // Randevu sil
    delete: async (id: string): Promise<void> => {
        try {
            const docRef = doc(db, COLLECTION_NAME, id);
            await deleteDoc(docRef);
        } catch (error) {
            throw error;
        }
    },

    // Kullanıcının randevularını getir
    getByUserId: async (userId: string): Promise<Appointment[]> => {
        try {
            const appointmentsRef = collection(db, COLLECTION_NAME);
            const q = query(
                appointmentsRef, 
                where('userId', '==', userId),
                limit(10)
            );
            const querySnapshot = await getDocs(q);
            return querySnapshot.docs.map(doc => {
                const data = doc.data();
                return {
                    id: doc.id,
                    userId: data.userId || '',
                    userName: data.name || '',
                    userEmail: data.email || '',
                    userPhone: data.phone || '',
                    date: data.date || '',
                    time: data.time || '',
                    status: data.status || 'pending',
                    paymentType: (data.paymentType || 'regular') as 'regular' | 'points',
                    paymentStatus: data.paymentStatus || 'waiting_confirmation',
                    notes: data.notes || '',
                    receiptUrl: data.receiptUrl || '',
                    receiptFileId: data.receiptFileId || '',
                    spreadId: data.spreadId || '',
                    spreadPoints: data.spreadPoints || 0,
                    createdAt: data.createdAt || Timestamp.now(),
                    updatedAt: data.updatedAt || Timestamp.now(),
                    rejectionReason: data.rejectionReason || ''
                };
            }).sort((a, b) => {
                // Sort by creation date (newest first)
                return b.createdAt.toMillis() - a.createdAt.toMillis();
            });
        } catch (error) {
            errorHandler.handleError(error, 'Error getting appointments');
            return [];
        }
    },

    // Belirli bir tarihteki randevuları getir
    getByDate: async (date: Date): Promise<Appointment[]> => {
        try {
            // Tarihi yyyy-MM-dd formatına çevir (Türkçe locale ile)
            const dateStr = format(date, 'yyyy-MM-dd');
            errorHandler.log('Getting appointments for date:', dateStr);

            const q = query(
                collection(db, COLLECTION_NAME),
                where('date', '==', dateStr)
            );
            const querySnapshot = await getDocs(q);
            
            return querySnapshot.docs.map(doc => {
                const data = doc.data();
                try {
                    return {
                        id: doc.id,
                        userId: data.userId || '',
                        userName: data.name || '',
                        userEmail: data.email || '',
                        userPhone: data.phone || '',
                        date: data.date || '',
                        time: data.time || '',
                        status: data.status || 'pending',
                        paymentType: (data.paymentType || 'regular') as 'regular' | 'points',
                        paymentStatus: data.paymentStatus || 'waiting_confirmation',
                        notes: data.notes || '',
                        receiptUrl: data.receiptUrl || '',
                        receiptFileId: data.receiptFileId || '',
                        spreadId: data.spreadId || '',
                        spreadPoints: data.spreadPoints || 0,
                        createdAt: data.createdAt || Timestamp.now(),
                        updatedAt: data.updatedAt || Timestamp.now(),
                        rejectionReason: data.rejectionReason || ''
                    };
                } catch (err) {
                    errorHandler.handleError(err, `Error processing appointment data for ${doc.id}`);
                    return {
                        id: doc.id,
                        userId: data.userId || '',
                        userName: data.name || '',
                        userEmail: data.email || '',
                        userPhone: data.phone || '',
                        date: format(new Date(), 'yyyy-MM-dd'),
                        time: '00:00',
                        status: 'pending',
                        paymentType: 'regular',
                        paymentStatus: 'waiting_confirmation',
                        notes: '',
                        receiptUrl: '',
                        receiptFileId: '',
                        spreadId: data.spreadId || '',
                        spreadPoints: data.spreadPoints || 0,
                        createdAt: Timestamp.now(),
                        updatedAt: Timestamp.now(),
                        rejectionReason: ''
                    };
                }
            });
        } catch (error) {
            errorHandler.handleError(error, 'Error getting appointments by date');
            return [];
        }
    },

    // Tüm randevuları getir (admin için)
    getAll: async (): Promise<RawAppointmentData[]> => {
        try {
            const q = query(
                collection(db, COLLECTION_NAME),
                orderBy('date', 'desc')
            );
            const querySnapshot = await getDocs(q);
            
            return querySnapshot.docs.map(doc => {
                const data = doc.data();
                return {
                    id: doc.id,
                    userId: data.userId,
                    name: data.name || '',
                    email: data.email || '',
                    phone: data.phone || '',
                    date: data.date,
                    time: data.time,
                    status: data.status || 'pending',
                    paymentType: data.paymentType || 'regular',
                    paymentStatus: data.paymentStatus || 'waiting_confirmation',
                    amount: data.amount || 0,
                    notes: data.notes || '',
                    receiptUrl: data.receiptUrl || '',
                    receiptFileId: data.receiptFileId || '',
                    createdAt: data.createdAt,
                    updatedAt: data.updatedAt,
                    spreadId: data.spreadId || '',
                    spreadPoints: data.spreadPoints || 0,
                    rejectionReason: data.rejectionReason || ''
                };
            });
        } catch (error) {
            throw error;
        }
    },

    // Müsait randevu saatlerini getir
    getAvailableTimeSlots: async (selectedDate: Date): Promise<TimeSlot[]> => {
        try {
            // Oturum kontrolü
            const currentUser = authService.getCurrentUser();
            errorHandler.log('Current user:', currentUser);
            
            if (!currentUser) {
                throw new Error('Randevu saatlerini görüntülemek için giriş yapmalısınız');
            }

            // Seçilen tarihi yyyy-MM-dd formatına çevir
            const selectedDateStr = format(selectedDate, 'yyyy-MM-dd');
            errorHandler.log('Selected date:', selectedDateStr);
            
            // O gün için mevcut randevuları getir
            const appointments = await appointmentService.getByDate(selectedDate);
            errorHandler.log('Appointments for date:', appointments);
            
            // Dolu saatleri ve durumlarını map'e ekle
            const bookedTimes = new Map<string, { paymentStatus: 'waiting_confirmation' | 'confirmed' | 'cancelled' }>();
            
            for (const appointment of appointments) {
                try {
                    // Sadece iptal edilmiş (rejected) randevular hariç tüm randevuları dolu olarak işaretle
                    if (appointment.status !== 'rejected' && appointment.paymentStatus !== 'cancelled') {
                        bookedTimes.set(appointment.time, { 
                            paymentStatus: appointment.paymentStatus 
                        });
                        errorHandler.log(`Booked time slot: ${appointment.time} for date ${selectedDateStr} with status ${appointment.paymentStatus}`);
                    }
                } catch (err) {
                    errorHandler.handleError(err, `Error processing appointment: ${appointment.id}`);
                }
            }

            // Çalışma saatlerini al
            let workingHours = await workingHoursService.getWorkingHoursForDate(selectedDateStr);
            errorHandler.log('Working hours:', workingHours);

            if (!workingHours || !workingHours.start || !workingHours.end) {
                errorHandler.log('Using default working hours');
                workingHours = {
                    start: '13:00',
                    end: '22:00'
                };
            }

            // Çalışma saatleri aralığında tüm saatleri oluştur
            const slots: TimeSlot[] = [];
            
            // Başlangıç ve bitiş saatlerini parse et ve validate et
            const parseAndValidateTime = (timeStr: string, defaultHour: number): { hour: number, minute: number } => {
                try {
                    const [hourStr = String(defaultHour), minuteStr = '00'] = (timeStr || '').split(':');
                    const hour = parseInt(hourStr, 10);
                    const minute = parseInt(minuteStr, 10);
                    
                    if (isNaN(hour) || hour < 0 || hour > 23) {
                        errorHandler.log(`Invalid hour value: ${hour}, using default: ${defaultHour}`);
                        return { hour: defaultHour, minute: 0 };
                    }
                    if (isNaN(minute) || minute < 0 || minute > 59) {
                        errorHandler.log(`Invalid minute value: ${minute}, using default: 0`);
                        return { hour, minute: 0 };
                    }
                    return { hour, minute };
                } catch (error) {
                    errorHandler.handleError(error, `Error parsing time: ${timeStr}`);
                    return { hour: defaultHour, minute: 0 };
                }
            };

            const startTime = parseAndValidateTime(workingHours.start, 13);
            const endTime = parseAndValidateTime(workingHours.end, 22);

            errorHandler.log('Parsed working hours:', { startTime, endTime });

            // Normal çalışma saatleri için slotları oluştur (1 saat aralıklarla)
            for (let hour = startTime.hour; hour <= endTime.hour; hour++) {
                const timeString = `${String(hour).padStart(2, '0')}:00`;
                const bookedInfo = bookedTimes.get(timeString);
                
                slots.push({
                    id: `${selectedDateStr}-${timeString}`,
                    time: timeString,
                    date: selectedDateStr,
                    status: bookedInfo ? 'booked' : 'available',
                    isActive: !bookedInfo
                });
            }

            // timeSlots koleksiyonundan özel slotları getir
            const timeSlotsRef = collection(db, 'timeSlots');
            const q = query(
                timeSlotsRef,
                where('date', '==', selectedDateStr)
            );
            const timeSlotsSnapshot = await getDocs(q);
            
            errorHandler.log('Manual time slots count:', timeSlotsSnapshot.size);
            
            // Özel slotları işle ve mevcut slotları güncelle
            for (const doc of timeSlotsSnapshot.docs) {
                try {
                    const slotData = doc.data();
                    const timeString = slotData.time;
                    
                    // Zaman formatını doğrula
                    const [slotHour = '00', slotMinute = '00'] = (timeString || '').split(':');
                    const validTimeString = `${String(parseInt(slotHour, 10) || 0).padStart(2, '0')}:${String(parseInt(slotMinute, 10) || 0).padStart(2, '0')}`;
                    
                    const bookedInfo = bookedTimes.get(validTimeString);
                    
                    // Mevcut slotu bul
                    const existingSlotIndex = slots.findIndex(slot => slot.time === validTimeString);
                    
                    if (existingSlotIndex !== -1) {
                        // Mevcut slotu güncelle
                        slots[existingSlotIndex] = {
                            id: doc.id,
                            time: validTimeString,
                            date: selectedDateStr,
                            status: bookedInfo ? 'booked' : (slotData.status || 'available'),
                            isActive: bookedInfo ? false : (slotData.isActive ?? true)
                        };
                    } else {
                        // Yeni slot ekle (çalışma saatleri dışındaki özel slotlar için)
                        slots.push({
                            id: doc.id,
                            time: validTimeString,
                            date: selectedDateStr,
                            status: bookedInfo ? 'booked' : (slotData.status || 'available'),
                            isActive: bookedInfo ? false : (slotData.isActive ?? true)
                        });
                    }
                } catch (err) {
                    errorHandler.handleError(err, `Error processing time slot: ${doc.id}`);
                }
            }

            // Saatleri sırala
            const sortedSlots = slots.sort((a, b) => {
                const [aHour = 0] = a.time.split(':').map(Number);
                const [bHour = 0] = b.time.split(':').map(Number);
                return aHour - bHour;
            });

            errorHandler.log('Final slots count:', sortedSlots.length);
            return sortedSlots;
        } catch (error) {
            errorHandler.handleError(error, 'Error getting available time slots');
            throw error;
        }
    },

    // Bekleyen ödeme sayısını getir
    getPendingPaymentCount: async (userId: string): Promise<number> => {
        try {
            const q = query(
                collection(db, COLLECTION_NAME),
                where('paymentStatus', '==', 'waiting_confirmation'),
                where('userId', '==', userId)
            );
            const snapshot = await getDocs(q);
            return snapshot.size;
        } catch (error) {
            throw error;
        }
    },

    // Saat formatını düzenle
    formatTime: (time: string): string => {
        try {
            const [hours, minutes] = time.split(':');
            return `${hours.padStart(2, '0')}:${minutes || '00'}`;
        } catch (error) {
            throw error;
        }
    },

    // Dekont yükleme
    uploadReceipt: async (file: File, phone: string): Promise<{ fileId: string; downloadUrl: string; fileName: string }> => {
        try {
            // Dosya boyutu kontrolü
            if (file.size > 5 * 1024 * 1024) {
                throw new Error('Dosya boyutu 5MB\'ı geçemez');
            }

            // Telefon numarasını ID olarak kullan
            const sanitizedPhone = phone.replace(/[^0-9]/g, ''); // Sadece rakamları al
            
            // Dosya uzantısını al
            const extension = file.name.split('.').pop() || '';
            
            // Dosya adını oluştur (telefon_timestamp.uzanti)
            const timestamp = Date.now();
            const fileName = `receipts/${sanitizedPhone}_${timestamp}.${extension}`;

            // Firebase Storage referansı oluştur
            const storageRef = ref(storage, fileName);

            // Dosyayı yükle
            await uploadBytes(storageRef, file, {
                customMetadata: {
                    phoneNumber: sanitizedPhone,
                    uploadTime: new Date().toISOString()
                }
            });

            // İndirme URL'si al
            const downloadUrl = await getDownloadURL(storageRef);

            // Firebase'deki appointments koleksiyonunu güncelle
            const appointmentsRef = collection(db, COLLECTION_NAME);
            const q = query(appointmentsRef, where('phone', '==', phone));
            const querySnapshot = await getDocs(q);

            if (!querySnapshot.empty) {
                const appointmentDoc = querySnapshot.docs[0];
                await updateDoc(doc(db, COLLECTION_NAME, appointmentDoc.id), {
                    receiptFileId: fileName,
                    receiptUrl: downloadUrl,
                    updatedAt: serverTimestamp()
                });
            }
            
            return { 
                fileId: sanitizedPhone,
                downloadUrl, 
                fileName 
            };
        } catch (error) {
            errorHandler.handleError(error, 'Error uploading receipt');
            throw error;
        }
    },

    // Dekont URL'si yenileme
    refreshReceiptUrl: async (fileName: string): Promise<string> => {
        try {
            return await getReceiptDownloadUrl(fileName);
        } catch (error) {
            errorHandler.handleError(error, 'Error refreshing receipt URL');
            throw error;
        }
    }
};

// Hata yönetimi için yardımcı fonksiyon
const handleError = (error: any) => {
  // Sessizce hataları yönet
  if (process.env.NODE_ENV !== 'production') {
    // Development ortamında bile hataları gösterme
    return;
  }
}; 